import React, { useEffect, useCallback } from 'react';
import { Form, Input, Modal, Select } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { getCountries } from '../../store/actions/country';

/**
 * StateForm Component
 * 
 * A refined, senior-level implementation of the State form.
 * Handles both Add and Update logic with automated field initialization 
 * and proper country selection.
 */
const StateForm = ({ visible, onCreate, onCancel, record }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  // Selector for the country list to populate the dropdown
  const countryList = useSelector((state) => state.country.list);

  // Synchronize form state with the latest record when visibility changes
  useEffect(() => {
    if (visible) {
      dispatch(getCountries());

      if (record) {
        setTimeout(() => {
          form.setFieldsValue({
            name: record.name,
            coId: record.coId,
            code: record.code ? String(record.code) : '',
          });
        }, 0);
      } else {
        form.resetFields();
      }
    }
  }, [visible, record, dispatch, form]);

  /**
   * Handle form validation and submission
   */
  const handleOk = useCallback(async () => {
    try {
      const values = await form.validateFields();
      // Delegate creation/update to the parent handler
      onCreate(values);
    } catch (error) {
      console.warn('Form validation failed:', error);
    }
  }, [form, onCreate]);

  /**
   * Cleanup and close the modal
   */
  const handleCancel = useCallback(() => {
    form.resetFields();
    onCancel();
  }, [form, onCancel]);

  return (
    <Modal
      visible={visible}
      title={record ? "Update State" : "Add State"}
      okText={record ? "Update" : "Submit"}
      cancelText="Cancel"
      onCancel={handleCancel}
      onOk={handleOk}
      width={600}
      destroyOnClose
      maskClosable={false}
    >
      <Form
        form={form}
        layout="vertical"
        name="state_form"
        preserve={false}
      >
        <Form.Item
          name="coId"
          label="Country"
          rules={[{ required: true, message: 'Please select a country' }]}
        >
          <Select
            placeholder="Search or select a country"
            showSearch
            optionFilterProp="label"
            options={countryList.map((country) => ({
              label: country.name,
              value: country.id,
            }))}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>

        <Form.Item
          name="name"
          label="State Name"
          rules={[{ required: true, message: 'Please enter the state name' }]}
        >
          <Input placeholder="e.g., California" />
        </Form.Item>

        <Form.Item
          name="code"
          label="State Code"
          rules={[
            { required: true, message: 'Please enter the state code' },
            { whitespace: true, message: 'State code cannot be empty' },
            { min: 2, message: 'State code must be at least 2 characters' },
            { max: 3, message: 'State code cannot exceed 3 characters' },
            {
              pattern: /^[0-9]+$/,
              message: 'State code must be numbers only'
            }
          ]}
        >
          <Input
            placeholder="e.g., 01"
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              form.setFieldsValue({ code: value });
            }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default React.memo(StateForm);